import { Booking, MeetingBooking } from '../types/models';
import { supabase } from './supabase';
import { USER_CACHE_TTL_MS } from './userService';

// 반복 예약 삭제 유형 정의
export type RecurrenceDeleteType = 'THIS_EVENT' | 'THIS_AND_FOLLOWING' | 'ALL_EVENTS';

// 예약 조회에 실제로 필요한 컬럼 (select('*') 대신 명시해 응답 크기를 줄인다)
const BOOKING_COLUMNS =
  'id, room_id, room_name, title, description, start_time, end_time, user_id, attendee_uids, color, is_reminder_enabled, reminder_minutes, parent_booking_id, recurrence_type, recurrence_end_date';

// 기본 조회 범위: 과거 12개월 ~ 미래 12개월
const DEFAULT_LOOKBACK_MONTHS = 12;
const DEFAULT_LOOKAHEAD_MONTHS = 12;

export interface BookingRange {
  from?: Date;
  to?: Date;
}

/**
 * DB의 중복 예약 방지 제약에 걸린 오류인지 판별한다.
 * 23P01 = exclusion_violation (bookings_no_room_time_overlap)
 * 23505 = unique_violation
 * 제약 정의는 supabase/migrations/20261019000000_prevent_duplicate_bookings.sql 참고
 */
const isOverlapViolation = (error: any): boolean =>
  error?.code === '23P01' || error?.code === '23505';

/**
 * 중복 예약 제약에 걸렸을 때 던질 오류.
 *
 * tsconfig의 target이 es5라 Error를 상속한 클래스는 instanceof가 제대로 동작하지 않는다.
 * 그래서 클래스 대신 플래그를 붙여 isBookingOverlapError로 판별한다.
 */
const createOverlapError = (): Error =>
  Object.assign(
    new Error('이미 해당 시간에 같은 회의실 예약이 있습니다. 새로고침 후 다시 확인해 주세요.'),
    { isBookingOverlap: true }
  );

/** 같은 회의실·시간대에 이미 예약이 있어서 거부된 오류인지 */
export const isBookingOverlapError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as any).isBookingOverlap === true;

/**
 * 예약 정보 가져오기
 * 범위를 지정하지 않으면 과거 12개월 ~ 미래 12개월 예약만 조회한다.
 * (테이블 전체 조회는 갱신마다 전송량이 계속 커져 Supabase egress를 빠르게 소진시킨다)
 */
export const getBookings = async (range?: BookingRange): Promise<Booking[]> => {
  const now = new Date();
  const from =
    range?.from ?? new Date(now.getFullYear(), now.getMonth() - DEFAULT_LOOKBACK_MONTHS, 1);
  const to =
    range?.to ??
    new Date(now.getFullYear(), now.getMonth() + DEFAULT_LOOKAHEAD_MONTHS + 1, 0, 23, 59, 59);

  const { data, error } = await supabase
    .from('bookings')
    .select(BOOKING_COLUMNS)
    .gte('start_time', from.toISOString())
    .lte('start_time', to.toISOString());

  if (error) {
    console.error('Error fetching bookings:', error);
    throw error;
  }

  console.log(`회의 예약 불러오기 성공: ${(data || []).length}건`);

  // 문자열 날짜를 Date 객체로 변환하고 로그 출력
  return (data || []).map(booking => {
    if (!booking.room_id) throw new Error('room_id is missing in booking');
    const convertedBooking = {
      ...booking,
      startTime: new Date(booking.start_time),
      endTime: new Date(booking.end_time),
      roomId: booking.room_id,
      roomName: booking.room_name,
      userId: booking.user_id || '',
      attendee_uids: booking.attendee_uids || [],
      color: booking.color || '',
      is_reminder_enabled: booking.is_reminder_enabled !== undefined ? booking.is_reminder_enabled : true,
      reminder_minutes: booking.reminder_minutes,
      recurrence_type: booking.recurrence_type,
      recurrence_end_date: booking.recurrence_end_date ? new Date(booking.recurrence_end_date) : undefined,
      parent_booking_id: booking.parent_booking_id,
    };
    return convertedBooking;
  });
};

/**
 * 새로운 예약 생성
 */
export const createBooking = async (booking: Omit<Booking, 'id'>): Promise<Booking> => {
  try {
    // camelCase를 snake_case로 변환하여 데이터베이스 스키마에 맞게 변환
    const bookingData = {
      title: booking.title,
      description: booking.description || '',
      start_time: booking.startTime,
      end_time: booking.endTime,
      user_id: booking.userId,
      room_id: booking.roomId,
      room_name: booking.roomName,
      attendee_uids: booking.attendee_uids || [],
      color: booking.color || '',
      is_reminder_enabled: booking.is_reminder_enabled !== undefined ? booking.is_reminder_enabled : true,
      reminder_minutes: booking.reminder_minutes,
      recurrence_type: booking.recurrence_type,
      recurrence_end_date: booking.recurrence_end_date,
      parent_booking_id: booking.parent_booking_id
    };
    // 첫 번째 예약 생성
    const { data, error } = await supabase
      .from('bookings')
      .insert([bookingData])
      .select()
      .single();
    if (error) {
      console.error('Error creating booking:', error);
      // 더블클릭이나 동시 예약으로 DB의 중복 방지 제약에 걸린 경우
      if (isOverlapViolation(error)) {
        throw createOverlapError();
      }
      throw error;
    }
    // 반복 예약 처리 부분 완전히 제거
    // 데이터베이스에서 가져온 데이터를 애플리케이션 형식으로 변환
    const convertedBooking = {
      ...data,
      startTime: new Date(data.start_time),
      endTime: new Date(data.end_time),
      roomName: data.room_name,
      userEmail: data.user_email,
      userId: data.user_id
    } as Booking;
    return convertedBooking;
  } catch (error) {
    console.error('예약 생성 중 오류 발생:', error);
    throw error;
  }
}

// 반복 인스턴스 생성 함수들
function getWeeklyInstances(start: Date, end: Date, endDate: Date) {
  const durationMs = end.getTime() - start.getTime();
  const instances = [];
  let currentStart = new Date(start);
  let currentEnd = new Date(end);
  while (currentStart <= endDate) {
    instances.push({ start: new Date(currentStart), end: new Date(currentEnd) });
    currentStart = new Date(currentStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    currentEnd = new Date(currentStart.getTime() + durationMs);
  }
  return instances;
}

function getBiWeeklyInstances(start: Date, end: Date, endDate: Date) {
  const durationMs = end.getTime() - start.getTime();
  const instances = [];
  let currentStart = new Date(start);
  let currentEnd = new Date(end);
  while (currentStart <= endDate) {
    instances.push({ start: new Date(currentStart), end: new Date(currentEnd) });
    currentStart = new Date(currentStart.getTime() + 14 * 24 * 60 * 60 * 1000);
    currentEnd = new Date(currentStart.getTime() + durationMs);
  }
  return instances;
}

function getMonthlyInstances(start: Date, end: Date, endDate: Date) {
  const durationMs = end.getTime() - start.getTime();
  const instances = [];
  // 첫 인스턴스
  instances.push({ start: new Date(start), end: new Date(end) });
  // N번째 요일 정보
  const weekday = start.getDay();
  const date = start.getDate();
  const n = Math.floor((date - 1) / 7) + 1;
  let year = start.getFullYear();
  let month = start.getMonth();
  while (true) {
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
    const firstDay = new Date(year, month, 1);
    const firstWeekday = firstDay.getDay();
    let day = 1 + ((7 + weekday - firstWeekday) % 7) + (n - 1) * 7;
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    if (day > lastDayOfMonth) continue; // 그 달에 N번째 요일이 없음
    const instStart = new Date(year, month, day, start.getHours(), start.getMinutes(), 0, 0);
    if (instStart > endDate) break;
    const instEnd = new Date(instStart.getTime() + durationMs);
    instances.push({ start: instStart, end: instEnd });
  }
  return instances;
}

/**
 * 반복 예약 생성(중복 인스턴스 제외)
 * @param booking 원본 예약 정보
 * @returns { created: Booking[], skipped: {start: Date, end: Date}[] }
 */
export const createRecurringBookingWithConflictFilter = async (booking: Omit<Booking, 'id'>) => {
  // 기존 예약 목록 불러오기
  const existingBookings = await getBookings();
  const {
    title, description, roomName, roomId, attendee_uids, color, is_reminder_enabled, reminder_minutes, recurrence_type, recurrence_end_date, startTime, endTime, userId
  } = booking;
  const originalStart = new Date(startTime);
  const originalEnd = new Date(endTime);
  const endDate = new Date(recurrence_end_date as Date);
  endDate.setHours(23, 59, 59);

  let allInstances: { start: Date, end: Date }[] = [];
  if (recurrence_type === 'WEEKLY') {
    allInstances = getWeeklyInstances(originalStart, originalEnd, endDate);
  } else if (recurrence_type === 'BI_WEEKLY') {
    allInstances = getBiWeeklyInstances(originalStart, originalEnd, endDate);
  } else if (recurrence_type === 'MONTHLY') {
    allInstances = getMonthlyInstances(originalStart, originalEnd, endDate);
  } else {
    allInstances = [{ start: new Date(originalStart), end: new Date(originalEnd) }];
  }

  // 중복 없는 인스턴스만 예약 생성
  const created: Booking[] = [];
  const skipped: { start: Date, end: Date }[] = [];
  let parentId: string | undefined = undefined;
  for (let i = 0; i < allInstances.length; i++) {
    const inst = allInstances[i];
    // created, existingBookings에는 반드시 Booking 타입만 들어가도록 보장
    const bookingsForConflictCheck: Booking[] = [...existingBookings, ...created].filter(b => {
      if (!b.roomId) {
        console.warn('roomId 없는 booking이 발견되어 중복 체크에서 제외됨', b);
        return false;
      }
      return true;
    });
    if (checkRoomConflict(roomId, inst.start, inst.end, bookingsForConflictCheck)) {
      skipped.push({ start: inst.start, end: inst.end });
      continue;
    }
    // 첫 예약은 parentId 없이, 이후는 parent_booking_id로 연결
    const bookingData: Omit<Booking, 'id'> = {
      title,
      description,
      startTime: inst.start,
      endTime: inst.end,
      roomName,
      roomId,
      attendee_uids,
      color,
      is_reminder_enabled,
      reminder_minutes,
      recurrence_type,
      recurrence_end_date,
      userId,
      parent_booking_id: parentId
    };
    // 메모리상의 목록으로는 통과했어도 DB 제약에 걸릴 수 있다 (다른 사람이 먼저 잡은 경우 등).
    // 반복 예약은 겹치는 건만 빼고 나머지를 만드는 정책이므로, 여기서도 건너뛰고 계속한다.
    let result: Booking;
    try {
      result = await createBooking(bookingData);
    } catch (error) {
      if (isBookingOverlapError(error)) {
        skipped.push({ start: inst.start, end: inst.end });
        continue;
      }
      throw error;
    }
    if (!parentId) parentId = result.id;
    created.push(result);
  }
  return { created, skipped };
};

/**
 * 예약 정보 업데이트
 */
export const updateBooking = async (id: string, booking: Partial<Booking>): Promise<Booking> => {
  // camelCase를 snake_case로 변환하여 데이터베이스 스키마에 맞게 변환
  const updateData: Record<string, any> = {};
  
  if (booking.title !== undefined) updateData.title = booking.title;
  if (booking.description !== undefined) updateData.description = booking.description;
  if (booking.startTime !== undefined) updateData.start_time = booking.startTime;
  if (booking.endTime !== undefined) updateData.end_time = booking.endTime;
  if (booking.roomId !== undefined) updateData.room_id = booking.roomId;
  if (booking.roomName !== undefined) updateData.room_name = booking.roomName;
  if (booking.attendee_uids !== undefined) updateData.attendee_uids = booking.attendee_uids; // attendees에서 attendee_uids로 변경
  // status 필드 제거 - 데이터베이스에 없음
  if (booking.color !== undefined) updateData.color = booking.color;
  if (booking.is_reminder_enabled !== undefined) updateData.is_reminder_enabled = booking.is_reminder_enabled;
  if (booking.reminder_minutes !== undefined) updateData.reminder_minutes = booking.reminder_minutes;
  if (booking.recurrence_type !== undefined) updateData.recurrence_type = booking.recurrence_type;
  if (booking.recurrence_end_date !== undefined) updateData.recurrence_end_date = booking.recurrence_end_date;
  if (booking.parent_booking_id !== undefined) updateData.parent_booking_id = booking.parent_booking_id;

  
  const { data, error } = await supabase
    .from('bookings')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating booking:', error);
    console.error('Error details:', error.message);
    console.error('Error code:', error.code);
    console.error('Error hint:', error.hint);
    console.error('Error details:', error.details);
    console.error('Booking data sent:', updateData);
    console.error('Booking ID:', id);
    // 수정으로 다른 예약과 시간이 겹치게 된 경우 DB 제약에 걸린다
    if (isOverlapViolation(error)) {
      throw createOverlapError();
    }
    throw error;
  }

  // 데이터베이스에서 가져온 데이터를 애플리케이션 형식으로 변환
  const convertedBooking = {
    ...data,
    startTime: new Date(data.start_time),
    endTime: new Date(data.end_time),
    roomName: data.room_name,
    userEmail: data.user_email,
    userId: data.user_id
  } as Booking;

  console.log('회의 예약 수정 성공:', convertedBooking);

  return convertedBooking;
};

/**
 * 진행 중인 회의 종료 (이메일 없이 end_time만 업데이트)
 * 현재 시각을 10분 단위로 올림하여 종료 시간으로 설정
 */
export const endMeetingNow = async (id: string): Promise<void> => {
  const now = new Date();
  const minutes = now.getMinutes();
  const remainder = minutes % 10;
  const roundedMinutes = remainder === 0 ? minutes : minutes + (10 - remainder);
  const endTime = new Date(now);
  // setMinutes(60)은 JS가 자동으로 다음 시간으로 올림 처리함
  endTime.setMinutes(roundedMinutes, 0, 0);

  const { error } = await supabase
    .from('bookings')
    .update({ end_time: endTime.toISOString(), is_early_end: true })
    .eq('id', id);

  if (error) {
    console.error('회의 종료 처리 실패:', error);
    throw error;
  }
};

/**
 * 예약 삭제
 * @param id 삭제할 예약 ID
 * @param recurrenceDeleteType 반복 예약 삭제 유형 (이 일정만, 이 일정 및 향후 일정, 모든 일정)
 */
export const deleteBooking = async (id: string, recurrenceDeleteType?: RecurrenceDeleteType): Promise<void> => {
  try {
    // 먼저 예약 정보를 가져와서 반복 예약인지 확인
    const { data: bookingData, error: fetchError } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', id)
      .single();

    if (fetchError) {
      console.error('예약 정보 조회 실패:', fetchError);
      throw fetchError;
    }

    // 반복 예약이 아니거나 recurrenceDeleteType이 지정되지 않은 경우 단일 예약만 삭제
    if (!recurrenceDeleteType || !bookingData.recurrence_type || bookingData.recurrence_type === 'NONE') {
      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('예약 삭제 실패:', error);
        throw error;
      }

      console.log('단일 예약 삭제 성공:', id);
      return;
    }

    // 반복 예약 삭제 처리
    switch (recurrenceDeleteType) {
      case 'THIS_EVENT': {
        // 이 일정만 삭제
        const { error } = await supabase
          .from('bookings')
          .delete()
          .eq('id', id);

        if (error) {
          console.error('단일 반복 예약 삭제 실패:', error);
          throw error;
        }
        console.log('단일 반복 예약 삭제 성공:', id);
        break;
      }

      case 'THIS_AND_FOLLOWING': {
        // 이 일정 및 향후 일정 삭제
        // 1. 현재 예약의 시작 시간 가져오기
        const currentStartTime = new Date(bookingData.start_time);
        
        // 2. 같은 부모를 가진 예약 중 현재 시간 이후의 모든 예약 삭제
        const parentId = bookingData.parent_booking_id || id;
        
        const { error } = await supabase
          .from('bookings')
          .delete()
          .or(`id.eq.${id},and(parent_booking_id.eq.${parentId},start_time.gte.${currentStartTime.toISOString()})`);

        if (error) {
          console.error('향후 반복 예약 삭제 실패:', error);
          throw error;
        }
        console.log('향후 반복 예약 삭제 성공');
        break;
      }

      case 'ALL_EVENTS': {
        // 모든 일정 삭제 (부모 예약과 모든 자식 예약 삭제)
        // 1. 부모 예약 ID 확인 (현재 예약이 자식인 경우 부모 ID 사용, 아니면 현재 ID가 부모)
        const parentId = bookingData.parent_booking_id || id;
        
        // 2. 부모 예약과 모든 자식 예약 삭제
        const { error } = await supabase
          .from('bookings')
          .delete()
          .or(`id.eq.${parentId},parent_booking_id.eq.${parentId}`);

        if (error) {
          console.error('모든 반복 예약 삭제 실패:', error);
          throw error;
        }
        console.log('모든 반복 예약 삭제 성공');
        break;
      }
    }
  } catch (error) {
    console.error('예약 삭제 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 예약 정보를 MeetingBooking 형식으로 변환
 * 각 예약에 대해 사용자 정보를 조회하여 추가
 */
export const bookingsToMeetings = async (bookings: Booking[]): Promise<MeetingBooking[]> => {
  if (!bookings || bookings.length === 0) {
    return [];
  }
  
  // 사용자 정보 캐시 객체 초기화 (중복 API 호출 방지)
  const userCache: Record<string, {name?: string, email?: string, position?: string}> = {};
  
  // 세션 스토리지에서 사용자 정보 불러오기 (position이 없는 항목은 제외)
  try {
    const cachedUsers = sessionStorage.getItem('cached_users');
    if (cachedUsers) {
      const parsedCache = JSON.parse(cachedUsers);
      const cacheTime = parsedCache._cacheTime || 0;
      if (Date.now() - cacheTime >= USER_CACHE_TTL_MS) {
        sessionStorage.removeItem('cached_users');
      } else {
        const cacheEntries = parsedCache.users || parsedCache;
      // 직위가 조회된 항목은 값이 비어 있어도 캐시로 인정한다.
      // (캐시에 저장할 때 직위 없는 사용자를 빈 문자열로 넣으므로,
      //  값의 참/거짓으로 판단하면 그런 사용자는 캐시가 영영 적중하지 않고 매번 재조회된다)
        Object.entries(cacheEntries).forEach(([id, data]: [string, any]) => {
          if (id !== '_cacheTime' && data.position !== undefined) {
            userCache[id] = data;
          }
        });
      }
    }
  } catch (e) {
    console.warn('캐시 데이터 불러오기 오류:', e);
  }
  
  // 중복되지 않는 사용자 ID 목록 추출 (예약자 + 참석자)
  const allUserIds: string[] = [];
  bookings.forEach(booking => {
    // 예약자 ID 추가
    if (booking.userId) {
      allUserIds.push(booking.userId);
    }
    // 참석자 IDs 추가 (빈 문자열 필터링)
    if (booking.attendee_uids && Array.isArray(booking.attendee_uids)) {
      booking.attendee_uids.forEach(uid => {
        if (uid && uid.trim() !== '') {  // 빈 문자열 제외
          allUserIds.push(uid);
        }
      });
    }
  });
  const uniqueUserIds = Array.from(new Set(allUserIds));
  
  // 캐시에 없는 사용자 ID 목록
  const userIdsToFetch = uniqueUserIds.filter(id => !userCache[id] || userCache[id].position === undefined);
  
  // 사용자 정보가 필요한 경우에만 API 호출
  if (userIdsToFetch.length > 0) {
    try {
      // auth.users의 user_metadata에서 사용자 정보 가져오기
      // 일반 사용자는 자신의 정보만 가져올 수 있으므로 현재 로그인한 사용자 정보만 가져옴
      const { data: authUser, error: authError } = await supabase.auth.getUser();
      
      if (authUser?.user && !authError) {
        const currentUserId = authUser.user.id;
        
        // 현재 로그인한 사용자의 이름/이메일만 임시 캐시 (position은 public.users에서 가져옴)
        if (userIdsToFetch.includes(currentUserId)) {
          const meta = authUser.user.user_metadata || {};
          userCache[currentUserId] = {
            name: meta.name || meta.display_name,
            email: authUser.user.email,
            // position은 undefined로 두어 public.users 테이블에서 조회되도록 함
          };
        }
      }
      
      // 나머지 사용자는 public.users 테이블에서 가져옴 (빈 문자열 제외)
      const remainingUserIds = userIdsToFetch.filter(id => (!userCache[id] || userCache[id].position === undefined) && id && id.trim() !== '');
      if (remainingUserIds.length > 0) {
        // is_active 조건 제거하여 퇴사자 포함 모든 사용자 조회
        const { data, error } = await supabase
          .from('users')
          .select('id, name, email, position, is_active')
          .in('id', remainingUserIds);

        if (data && !error) {
          // 조회된 사용자 정보를 캐시에 추가
          data.forEach(user => {
            userCache[user.id] = {
              name: user.name,
              email: user.email,
              position: user.position || ''
            };
          });

          // 캐시 업데이트
          try {
            sessionStorage.setItem('cached_users', JSON.stringify({
              _cacheTime: Date.now(),
              users: userCache
            }));
          } catch (e) {
            console.warn('캐시 저장 오류:', e);
          }
        } else if (error) {
          console.warn('사용자 정보 조회 실패:', error);
        }
      }
    } catch (error) {
      console.error('사용자 정보 조회 중 오류 발생:', error);
    }
  }
  
  // 모든 예약에 대해 사용자 정보 매핑
  const userInfoMap = uniqueUserIds.reduce((acc, userId) => {
    const userData = userCache[userId] || {};

    // 사용자 이름 결정: name > email 앞부분 > "(정보 없음)"
    const baseName = userData.name || userData.email?.split('@')[0];
    let userName = baseName;

    // 사용자 정보를 전혀 찾지 못한 경우
    if (!userName) {
      userName = '(정보 없음)';
      console.warn(`사용자 정보를 찾을 수 없습니다. userId: ${userId}`);
    }
    if (userName !== '(정보 없음)' && userData.position) {
      userName = `${userName} ${userData.position}`;
    }

    acc[userId] = {
      userId: userId,
      userName: userName,
      userEmail: userData.email || ''
    };
    return acc;
  }, {} as Record<string, {userId: string, userName: string, userEmail: string}>);
  
  // 사용자 정보와 예약 정보를 합쳐서 MeetingBooking 형태로 변환
  const meetings = bookings.map(booking => {
    const userInfo = userInfoMap[booking.userId] || {
      userId: booking.userId || '',
      userName: '(정보 없음)',
      userEmail: ''
    };

    const meeting = {
      id: booking.id,
      title: booking.title,
      start: booking.startTime,
      end: booking.endTime,
      backgroundColor: booking.color,
      borderColor: booking.color,
      textColor: '#ffffff',
      extendedProps: {
        roomName: booking.roomName,
        description: booking.description,
        attendee_uids: booking.attendee_uids || [], // attendees에서 attendee_uids로 변경
        userId: userInfo.userId,
        userName: userInfo.userName,
        userEmail: userInfo.userEmail,
        is_reminder_enabled: booking.is_reminder_enabled,
        reminder_minutes: booking.reminder_minutes,
        recurrence_type: booking.recurrence_type,
        recurrence_end_date: booking.recurrence_end_date,
        parent_booking_id: booking.parent_booking_id
      }
    };
    return meeting;
  });
  
  return meetings;
};

/**
 * 시간 겹침 유효성 검증
 * @param newStart 새 예약 시작 시간
 * @param newEnd 새 예약 종료 시간
 * @param existingBookings 기존 예약 목록
 * @param excludeBookingId (선택) 수정 시 자기 자신은 제외
 * @returns 겹치는 예약이 있으면 true, 없으면 false
 */
export function checkTimeConflict(
  newStart: Date,
  newEnd: Date,
  existingBookings: Booking[],
  excludeBookingId?: string
): boolean {
  return existingBookings.some(b => {
    if (excludeBookingId && b.id === excludeBookingId) return false;
    // 시간 겹침: (시작 < 기존 종료) && (종료 > 기존 시작)
    return newStart < b.endTime && newEnd > b.startTime;
  });
}

/**
 * 장소(회의실) 겹침 유효성 검증
 * @param newRoomId 새 예약 회의실 ID
 * @param newStart 새 예약 시작 시간
 * @param newEnd 새 예약 종료 시간
 * @param existingBookings 기존 예약 목록
 * @param excludeBookingId (선택) 수정 시 자기 자신은 제외
 * @returns 겹치는 예약이 있으면 true, 없으면 false
 */
export function checkRoomConflict(
  newRoomId: string,
  newStart: Date,
  newEnd: Date,
  existingBookings: Booking[],
  excludeBookingId?: string
): boolean {
  return existingBookings.some(b => {
    if (!b.roomId) throw new Error('roomId missing in booking');
    if (excludeBookingId && b.id === excludeBookingId) return false;
    if (b.roomId !== newRoomId) return false;
    // 시간 겹침: (시작 < 기존 종료) && (종료 > 기존 시작)
    return newStart < b.endTime && newEnd > b.startTime;
  });
}

/**
 * 반복 예약 겹침 유효성 검증 (겹치는 예약만 반환)
 * @param newRoomId 새 예약 회의실 ID
 * @param newStart 새 예약 시작 시간
 * @param newEnd 새 예약 종료 시간
 * @param existingBookings 기존 예약 목록
 * @returns 겹치는 예약 배열
 */
export function getRecurringConflicts(
  newRoomId: string,
  newStart: Date,
  newEnd: Date,
  existingBookings: Booking[]
): Booking[] {
  return existingBookings.filter(b => {
    if (b.roomId !== newRoomId) return false;
    // 시간 겹침: (시작 < 기존 종료) && (종료 > 기존 시작)
    return newStart < b.endTime && newEnd > b.startTime;
  });
}
